/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { Priority, Stage } from '@gh/builder-shared-data';
import {
  EnumDictionary,
  PlainImmutablePageState,
  setEntityField,
  setEntityFields,
} from '@gh/common-shared';
import { EntityVersion, ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { getBool, withoutIndex } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';

import { concat, find, findIndex, get, isEqual } from 'lodash';
import { Observable } from 'rxjs';

import {
  INIT_LEAD_BOARD_STATE_VALUE,
  initLeadBoardPageState,
  isLeadBoardItemHasSameColumnId,
  LEAD_BOARD_SCROLL_INIT,
  LEAD_BOARD_SCROLL_START,
  LeadBoardColumnId,
  LeadBoardInfo,
  LeadBoardListItem,
  LeadBoardMobileFilter,
  LeadBoardScroll,
  LeadBoardScrollTarget,
  LeadBoardState,
  SalespersonSelectItem,
} from '../../resources/lead-board.model';
import {
  buildLeadBoardTable,
  getLeadBoardColumnId,
  getLeadBoardItemId,
  leadBoardTable,
  queryIsBoardEmpty,
  queryLeadBoardPanelColumnList,
} from './lead-board.state';
import { VirtualPosition } from './virtual-rendering';

@Injectable()
export class LeadBoardFormState extends PlainImmutablePageState<LeadBoardState> {
  salesPersonId$ = this.select(({ info: { salesPersonId } }) => salesPersonId);
  salespersonList$ = combineLatest$(this.salesPersonId$, this.select(({ salespersonList }) => salespersonList)).pipe(
    map$(([salesPersonId, salespersonList]) => this.resortSalespersonList(salespersonList, salesPersonId)));
  salespersonName$ = combineLatest$(this.salesPersonId$, this.select(({ salespersonList }) => salespersonList)).pipe(
    map$(([salesPersonId, salespersonList]) =>
      get(find(salespersonList, { id: salesPersonId }), 'name', void 0)));
  showCompleted$ = this.select(({ info: { showCompleted } }) => showCompleted);
  showActive$ = this.select(({ info: { showActive } }) => showActive);
  showInactive$ = this.select(({ info: { showInactive } }) => showInactive);
  showOverdue$ = this.select(({ info: { showOverdue } }) => showOverdue);
  openedId$ = this.select(({ openedId }) => openedId);
  table$ = this.select(({ boardTable }) => boardTable);
  opened$ = this.select(({ openedId, boardTable }) => openedId ? leadBoardTable.byId(boardTable, openedId) : void 0);
  dragging$ = this.select(({ draggingId, boardTable }) =>
    draggingId ? leadBoardTable.byId(boardTable, draggingId) : void 0);
  filter$ = this.select(({ info }) => info);
  mobileFilter$ = this.select(({ mobileFilter }) => mobileFilter);
  currentMobilePriority$ = this.mobileFilter$.pipe(map$(({ priority }) => priority));
  currentMobileStage$ = this.mobileFilter$.pipe(map$(({ stage }) => stage));
  scroll$ = this.select(({ scroll }) => scroll);
  scrollPosition$ = this.scroll$.pipe(map$(({ target, position }) =>
    target !== LeadBoardScrollTarget.Standard ? LEAD_BOARD_SCROLL_START : position));
  mobileScrollPosition$ = this.scroll$.pipe(map$(({ target, position }) =>
    target !== LeadBoardScrollTarget.Mobile ? LEAD_BOARD_SCROLL_START : position));
  activeZoneId$ = this.select(({ activeDropZone }) => activeDropZone).pipe(
    map$((activeZone) => activeZone ? getLeadBoardColumnId(activeZone.priority, activeZone.stage) : void 0));

  isBoardEmpty$ = this.select(queryIsBoardEmpty);

  constructor() {
    super(INIT_LEAD_BOARD_STATE_VALUE);
  }

  get scrollPosition(): VirtualPosition {
    const { target, position } = this.value.scroll;
    return target !== LeadBoardScrollTarget.Standard ? LEAD_BOARD_SCROLL_START : position;
  }

  isOpened(item: LeadBoardListItem): boolean {
    return getLeadBoardItemId(item) === this.value.openedId;
  }

  /**
   * Build Lead board PlainImmutablePageState
   * @param salesPersonId {Maybe<ID>}
   * @param salespersonList {SalespersonSelectItem[]}
   * @param list {LeadBoardListItem[]}
   * @param priorities {EnumDictionary<Priority>}
   * @param stages {EnumDictionary<Stage>}
   * @param isEditMode {boolean}
   * @param leadBordFilter {any}
   */
  build(salesPersonId: Maybe<ID>,
        salespersonList: SalespersonSelectItem[],
        list: LeadBoardListItem[],
        priorities: EnumDictionary<Priority>,
        stages: EnumDictionary<Stage>,
        isEditMode: boolean,
        leadBordFilter: any): void {
    this.setMode(isEditMode ? FormMode.EditMode : FormMode.ViewMode);
    this.setValue(initLeadBoardPageState(
      salesPersonId,
      salespersonList,
      list,
      priorities,
      stages,
      leadBordFilter));
  }

  /**
   * Updates Lead board card followUpDate
   * @param card {LeadBoardListItem}
   * @param followUpDate {Date}
   */
  setSalesFolderFollowUpDate(card: LeadBoardListItem, followUpDate: Date): void {
    this.setField('boardTable', leadBoardTable.mergeById(
      this.value.boardTable,
      getLeadBoardItemId(card),
      (item) => setEntityField(item, 'followUpDate', followUpDate)));
  }

  /**
   * Refreshes Lead board card version
   * @param card {LeadBoardListItem}
   * @param version {Maybe<EntityVersion>}
   */
  refreshCardVersion(card: LeadBoardListItem, version: Maybe<EntityVersion>): void {
    this.setField('boardTable', leadBoardTable.mergeById(
      this.value.boardTable,
      getLeadBoardItemId(card),
      (item) => setEntityField(item, 'version', version)));
  }

  /**
   * Increments meetings count Card property value
   * @param card {LeadBoardListItem}
   */
  incrementCardActivitiesCount(card: LeadBoardListItem): void {
    this.setField('boardTable', leadBoardTable.mergeById(
      this.value.boardTable,
      getLeadBoardItemId(card),
      (item) => setEntityField(item, 'activityCount', item.activityCount + 1),
    ));
  }

  setItemAsDragged(card: LeadBoardListItem, dragged: boolean): void {
    this.setFields({
      'draggingId': dragged ? getLeadBoardItemId(card) : void 0,
      'boardTable': leadBoardTable.mergeById(
        this.value.boardTable,
        getLeadBoardItemId(card),
        (item) => setEntityField(item, 'isDragged', dragged),
      ),
    });
  }

  enterZone(columnId: LeadBoardColumnId, card: LeadBoardListItem): void {
    if (!isLeadBoardItemHasSameColumnId(card, columnId)) {
      this.setField('activeDropZone', columnId);
    }
  }

  exitZone(_: LeadBoardColumnId, card: LeadBoardListItem): void {
    this.setField('activeDropZone', void 0);
  }

  /**
   * Moves Lead board card between board columns or panels, between stages or priorities.
   * @param card {LeadBoardListItem}
   * @param stage {Stage}
   * @param priority {Priority}
   */
  moveCard(card: LeadBoardListItem, { stage, priority }: LeadBoardColumnId): void {
    this.setField('boardTable', leadBoardTable.mergeById(
      this.value.boardTable,
      getLeadBoardItemId(card),
      (item) => setEntityFields(item, { stage, priority })));
  }

  /**
   * Monitors by expanded state of Lead board cards
   * @param id {ID}
   */
  toggleCard(id: ID): void {
    this.setField('openedId', this.value.openedId === id ? void 0 : id);
  }

  refreshBoardList(list: LeadBoardListItem[]): void {
    this.setField('boardTable', buildLeadBoardTable(list));
  }

  setSalespersonId(salesPersonId: Maybe<ID>): void {
    this.updateInfo({ salesPersonId });
  }

  setSearchString(searchString: Maybe<string>): void {
    this.updateInfo({ searchString });
  }

  setInfoShowCompletedFilter(isVisible: boolean): void {
    this.updateInfo({showCompleted: isVisible});
  }

  setInfoShowActiveFilter(isVisible: boolean): void {
    this.updateInfo({showActive: isVisible});
  }

  setInfoShowInactiveFilter(isVisible: boolean): void {
    this.updateInfo({ showInactive: isVisible});
  }

  setInfoShowOverdueFilter(isVisible: boolean): void {
    this.updateInfo({showOverdue: isVisible});
  }

  setMobilePriority(priority: number): void {
    this.setField('mobileFilter', setEntityField(this.value.mobileFilter, 'priority', priority));
  }

  setMobileStage(stage: number): void {
    this.setField('mobileFilter', setEntityField(this.value.mobileFilter, 'stage', stage));
  }

  setMobileFilter(filter: LeadBoardMobileFilter): void {
    this.setField('mobileFilter', filter);
  }

  /**
   * Sets info value, board filter
   * @param filter {LeadBoardInfo}
   */
  setFilter(filter: any): void {
    for (const key in filter) {
      if (filter[key] === undefined) {
        delete filter[key];
      }
    }
    if (!isEqual(this.value.info, filter)) {
      this.setField('info', { ...this.value.info, ...filter });
    }
  }

  setScroll({ target, position }: LeadBoardScroll): void {
    const { scroll } = this.value;
    if (isEqual(position, LEAD_BOARD_SCROLL_START)) {
      this.setField('scroll', LEAD_BOARD_SCROLL_INIT);
    } else if (scroll.target !== target || !isEqual(scroll.position, position)) {
      this.setField('scroll', setEntityFields(this.value.scroll, { target, position }));
    }
  }

  resetScroll(): void {
    this.setField('scroll', LEAD_BOARD_SCROLL_INIT);
  }

  cardsFor$(priority: number, stage: number): Observable<LeadBoardListItem[]> {
    return this.select(queryLeadBoardPanelColumnList, priority, stage);
  }

  openedCardFor$(targetPriority: number, targetStage: number): Observable<Maybe<LeadBoardListItem>> {
    return this.opened$.pipe(map$((opened) =>
      opened && opened.priority === targetPriority && opened.stage === targetStage ? opened : void 0));
  }

  draggingCardFor$(targetPriority: number, targetStage: number): Observable<Maybe<LeadBoardListItem>> {
    return this.dragging$.pipe(map$((opened) =>
      opened && opened.priority === targetPriority && opened.stage === targetStage ? opened : void 0));
  }

  setStartFilter(filter: any): void {
    const model = {
      showInactive: getBool(filter.showInactive),
      showActive: getBool(filter.showActive),
      showOverdue: getBool(filter.showOverdue),
      showCompleted: getBool(filter.showCompleted),
      showSuppressed: getBool(filter.showSuppressed),
    };
    this.updateInfo(model);
  }
  /**
   * Updates Lead board Top form values
   * @param info {LeadBoardInfo}
   */
  private updateInfo(info: any): void {
    this.setField('info', {
      ...this.value.info,
      ...info,
    });
  }

  private resortSalespersonList(salespersonList: SalespersonSelectItem[], id: Maybe<ID>): SalespersonSelectItem[] {
    const salespersonIndex = findIndex(salespersonList, { id });
    return salespersonIndex > -1
      ? concat(
        salespersonList.slice(salespersonIndex, salespersonIndex + 1),
        withoutIndex(salespersonList, salespersonIndex))
      : salespersonList;
  }
}
